import { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';

const API = `${process.env.REACT_APP_BACKEND_URL}/api/data-quality`;
const MFG_API = `${process.env.REACT_APP_BACKEND_URL}/api/mfg`;

/**
 * 仮データ・不整合の点検（2026-10-21）。
 *
 * 自動では何も変えない。1行ずつ 今の値 → 直す値 を見せて、直すか決める。
 * 直したものはバッチ単位で元に戻せる。
 */
export const DataQualityAudit = () => {
  const [data, setData] = useState(null);
  const [edits, setEdits] = useState({});
  const [busy, setBusy] = useState('');
  const [history, setHistory] = useState([]);
  const [kind, setKind] = useState('all');
  const [pathDocs, setPathDocs] = useState(null);
  const [deleted, setDeleted] = useState(null);
  const [unitDupes, setUnitDupes] = useState(null);
  const [staleEq, setStaleEq] = useState(null);

  const deleteStale = async (id) => {
    setBusy(id || 'stale-all');
    try {
      const r = await axios.post(`${API}/stale-equipment/apply`,
        id ? { ids: [id] } : { all: true });
      toast.success(`${r.data.deleted}件を削除しました（元に戻せます）`);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || '削除できませんでした');
    } finally { setBusy(''); }
  };

  const undoStale = async (b) => {
    setBusy(b.id);
    try {
      await axios.post(`${API}/stale-equipment/undo/${b.id}`);
      toast.success('元に戻しました');
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || '戻せませんでした');
    } finally { setBusy(''); }
  };

  const load = useCallback(async () => {
    const [a, h, p, dl, ud, se] = await Promise.all([
      axios.get(`${API}/audit`).then((r) => r.data),
      axios.get(`${API}/history`).then((r) => r.data).catch(() => []),
      axios.get(`${API}/path-docs`).then((r) => r.data).catch(() => null),
      axios.get(`${MFG_API}/equipment/deleted`).then((r) => r.data).catch(() => null),
      axios.get(`${API}/unit-duplicates`).then((r) => r.data).catch(() => null),
      axios.get(`${API}/stale-equipment`).then((r) => r.data).catch(() => null),
    ]);
    setData(a);
    setHistory(h.filter((x) => !x.undone_at));
    setPathDocs(p);
    setDeleted(dl);
    setUnitDupes(ud);
    setStaleEq(se);
  }, []);

  const restoreEquipment = async (row) => {
    setBusy(row.id);
    try {
      await axios.post(`${MFG_API}/equipment/${row.id}/restore`);
      toast.success('元に戻しました（一覧に戻ります）');
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || '戻せませんでした');
    } finally { setBusy(''); }
  };

  const movePathDoc = async (row) => {
    setBusy(row.document_id);
    try {
      await axios.post(`${API}/path-docs/apply`, { document_id: row.document_id });
      toast.success('案件のパスに移しました（文書は保管・番号は欠番のまま）');
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || '移せませんでした');
    } finally { setBusy(''); }
  };

  const undoPathDoc = async (b) => {
    setBusy(b.id);
    try {
      await axios.post(`${API}/path-docs/undo/${b.id}`);
      toast.success('元に戻しました');
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || '戻せませんでした');
    } finally { setBusy(''); }
  };

  const fixUnitDupe = async (row) => {
    setBusy(row.key);
    try {
      const r = await axios.post(`${API}/unit-duplicates/apply`,
        { request_id: row.request_id, line_index: row.line_index });
      toast.success(`${r.data.kept}件を残し、${r.data.retired}件を廃棄（取消）にしました`);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || '直せませんでした');
    } finally { setBusy(''); }
  };

  const undoUnitDupe = async (b) => {
    setBusy(b.id);
    try {
      await axios.post(`${API}/unit-duplicates/undo/${b.id}`);
      toast.success('元に戻しました');
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || '戻せませんでした');
    } finally { setBusy(''); }
  };

  useEffect(() => { load().catch(() => {}); }, [load]);

  const fix = async (row) => {
    const value = edits[row.key] !== undefined ? edits[row.key] : (row.proposed ?? '');
    setBusy(row.key);
    try {
      await axios.post(`${API}/apply`, {
        register: row.register, item_id: row.item_id, field: row.field,
        value: String(value ?? ''), kind: row.kind,
      });
      toast.success('直しました（元に戻せます）');
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || '直せませんでした');
    } finally { setBusy(''); }
  };

  const undo = async (b) => {
    setBusy(b.id);
    try {
      await axios.post(`${API}/undo/${b.id}`);
      toast.success('元に戻しました');
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || '戻せませんでした');
    } finally { setBusy(''); }
  };

  if (!data) return null;
  const rows = kind === 'all' ? data.rows : data.rows.filter((r) => r.kind === kind);

  return (
    <section className="rounded-xl border border-zinc-200 bg-white p-4" data-testid="data-quality-audit">
      <h2 className="text-base font-semibold text-zinc-900">仮データ・不整合の点検</h2>
      <p className="mt-0.5 text-[11px] text-zinc-500" data-testid="dq-note">{data.note}</p>

      <div className="mt-3 flex flex-wrap gap-1.5" data-testid="dq-kind-chips">
        {[['all', `すべて（${data.rows.length}）`],
          ...Object.entries(data.kind_labels).map(([k, label]) => (
            [k, `${label}（${data.counts[k] || 0}）`]))].map(([k, label]) => (
            <button key={k} type="button" onClick={() => setKind(k)}
              data-testid={`dq-kind-${k}`}
              className={`rounded-full border px-3 py-1.5 text-[11px] font-medium transition-colors ${
                kind === k ? 'border-zinc-800 bg-zinc-800 text-white'
                  : 'border-zinc-200 bg-white text-zinc-600 hover:bg-zinc-50'}`}>
              {label}
            </button>
          ))}
      </div>

      {history.length > 0 && (
        <div className="mt-3 rounded-lg border border-sky-200 bg-sky-50 px-3 py-2" data-testid="dq-undo-list">
          <p className="text-[11px] font-semibold text-sky-900">直した分（元に戻せます）</p>
          {history.slice(0, 5).map((b) => (
            <div key={b.id} className="mt-1 flex items-center gap-2 text-[11px] text-sky-900">
              <span className="truncate">{b.label} · {b.field} · {String(b.before ?? '—')} → {String(b.after ?? '—')}</span>
              <Button size="sm" variant="outline" className="ml-auto h-7 text-[11px]"
                disabled={busy === b.id} onClick={() => undo(b)} data-testid={`dq-undo-${b.id}`}>
                元に戻す
              </Button>
            </div>
          ))}
        </div>
      )}

      {/* 2026-10-22 · パスだけの文書 → 案件のパスに移す */}
      {unitDupes && (
        <div className="mt-3 rounded-lg border border-zinc-200 bg-zinc-50/60 p-3" data-testid="dq-unit-dupes">
          <p className="text-[12px] font-semibold text-zinc-800">
            入荷完了の重複登録（{unitDupes.count}）
          </p>
          <p className="mt-0.5 text-[11px] text-zinc-500">{unitDupes.note}</p>
          {unitDupes.count === 0 ? (
            <p className="mt-1.5 text-[12px] text-zinc-400" data-testid="dq-unit-dupes-empty">なし</p>
          ) : (
            <ul className="mt-1.5 space-y-1" data-testid="dq-unit-dupes-rows">
              {unitDupes.rows.map((r) => (
                <li key={r.key} className="rounded border border-zinc-200 bg-white px-2 py-1.5"
                  data-testid={`dq-unit-dupe-${r.key}`}>
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-[12px] font-semibold text-zinc-800">
                      {r.request_no} {r.line_label}・{r.name}
                    </span>
                    <span className="text-[11px] text-zinc-500">
                      {r.register_label} 台数 {r.qty} / 有効 {r.active_count}
                    </span>
                    <button type="button" onClick={() => fixUnitDupe(r)} disabled={busy === r.key}
                      className="ml-auto rounded bg-zinc-900 px-2 py-1 text-[11px] font-semibold text-white disabled:opacity-50"
                      data-testid={`dq-unit-dupe-apply-${r.key}`}>
                      この行を直す
                    </button>
                  </div>
                  <p className="mt-0.5 text-[11px] text-zinc-600">
                    残す: {r.keep.map((k) => k.no).join('、')}
                    {' / '}廃棄（取消）: {r.retire.map((e) => e.no).join('、')}
                    {r.confirmed_price != null && ` / 確定単価 ¥${Number(r.confirmed_price).toLocaleString('ja-JP')} を残す分に写します`}
                  </p>
                </li>
              ))}
            </ul>
          )}
          {(unitDupes.applied || []).length > 0 && (
            <div className="mt-2 border-t border-zinc-200 pt-2" data-testid="dq-unit-dupes-undo">
              {unitDupes.applied.map((b) => (
                <div key={b.id} className="flex items-center gap-2 text-[11px] text-zinc-600">
                  <span>{b.label}・{b.name}（{b.retired}件を取消）</span>
                  <button type="button" onClick={() => undoUnitDupe(b)} disabled={busy === b.id}
                    className="ml-auto font-semibold text-zinc-700 underline disabled:opacity-50"
                    data-testid={`dq-unit-dupe-undo-${b.id}`}>
                    元に戻す
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* 10 · テスト用に作った設備（EQ-####）の後片づけ — 適用するまで消えない */}
      {staleEq && (
        <div className="mt-3 rounded-lg border border-zinc-200 bg-zinc-50/60 p-3" data-testid="dq-stale-eq">
          <p className="text-[12px] font-semibold text-zinc-800">
            テストで作った設備（{staleEq.count}）
          </p>
          <p className="mt-0.5 text-[11px] text-zinc-500">{staleEq.note}</p>
          {staleEq.count === 0 ? (
            <p className="mt-1.5 text-[12px] text-zinc-400" data-testid="dq-stale-eq-empty">なし</p>
          ) : (
            <>
              <button type="button" onClick={() => deleteStale(null)} disabled={busy === 'stale-all'}
                className="mt-1.5 rounded bg-zinc-900 px-2 py-1 text-[11px] font-semibold text-white disabled:opacity-50"
                data-testid="dq-stale-eq-apply-all">
                すべて削除（登録ミス）
              </button>
              <ul className="mt-1.5 space-y-1" data-testid="dq-stale-eq-rows">
                {staleEq.rows.map((r) => (
                  <li key={r.key} className="flex flex-wrap items-center gap-2 rounded border border-zinc-200 bg-white px-2 py-1.5"
                    data-testid={`dq-stale-eq-${r.id}`}>
                    <span className="font-mono text-[11px] text-zinc-600">{r.asset_no}</span>
                    <span className="text-[12px] text-zinc-800">{r.name}</span>
                    {r.blockers?.length > 0 && (
                      <span className="text-[11px] text-amber-700">
                        使われています: {r.blockers.join('・')}
                      </span>
                    )}
                    <button type="button" onClick={() => deleteStale(r.id)}
                      disabled={busy === r.id || !r.deletable}
                      className="ml-auto rounded bg-zinc-900 px-2 py-1 text-[11px] font-semibold text-white disabled:opacity-40"
                      data-testid={`dq-stale-eq-apply-${r.id}`}>
                      削除（登録ミス）
                    </button>
                  </li>
                ))}
              </ul>
            </>
          )}
          {(staleEq.applied || []).length > 0 && (
            <div className="mt-2 border-t border-zinc-200 pt-2" data-testid="dq-stale-eq-undo">
              {staleEq.applied.map((b) => (
                <div key={b.id} className="flex items-center gap-2 text-[11px] text-zinc-600">
                  <span>{b.count}件を削除（{(b.rows || []).map((x) => x.asset_no).join('、')}）</span>
                  <button type="button" onClick={() => undoStale(b)} disabled={busy === b.id}
                    className="ml-auto font-semibold text-zinc-700 underline disabled:opacity-50"
                    data-testid={`dq-stale-eq-undo-${b.id}`}>
                    元に戻す
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {pathDocs && (
        <div className="mt-3 rounded-lg border border-zinc-200 bg-zinc-50/60 p-3" data-testid="dq-path-docs">
          <p className="text-[12px] font-semibold text-zinc-800">
            パスだけの文書（{pathDocs.count}）
          </p>
          <p className="mt-0.5 text-[11px] text-zinc-500">{pathDocs.note}</p>
          {pathDocs.count === 0 ? (
            <p className="mt-1.5 text-[12px] text-zinc-400" data-testid="dq-path-docs-empty">なし</p>
          ) : (
            <ul className="mt-1.5 space-y-1" data-testid="dq-path-docs-rows">
              {pathDocs.rows.map((r) => (
                <li key={r.document_id} className="flex flex-wrap items-center gap-2 text-[12px]"
                  data-testid={`dq-path-doc-${r.document_id}`}>
                  <span className="font-mono text-[11px] text-zinc-500">{r.document_number || '番号なし'}</span>
                  <span className="font-medium text-zinc-900">{r.title}</span>
                  <span className="truncate font-mono text-[11px] text-zinc-500">{r.path}</span>
                  <span className="text-[11px] text-zinc-600">
                    案件: {r.developments.map((d) => d.name).join('・')}
                  </span>
                  <Button size="sm" variant="outline" className="ml-auto h-7 text-[11px]"
                    disabled={busy === r.document_id} onClick={() => movePathDoc(r)}
                    data-testid={`dq-path-doc-apply-${r.document_id}`}>
                    案件のパスに移す
                  </Button>
                </li>
              ))}
            </ul>
          )}
          {(pathDocs.applied || []).length > 0 && (
            <div className="mt-2 border-t border-zinc-200 pt-2" data-testid="dq-path-docs-undo">
              {pathDocs.applied.map((b) => (
                <div key={b.id} className="mt-1 flex items-center gap-2 text-[11px] text-zinc-700">
                  <span className="truncate">{b.document_number || '番号なし'} {b.title} → 案件のパス</span>
                  <Button size="sm" variant="outline" className="ml-auto h-7 text-[11px]"
                    disabled={busy === b.id} onClick={() => undoPathDoc(b)}
                    data-testid={`dq-path-doc-undo-${b.id}`}>元に戻す</Button>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* 2026-10-23 · 削除（登録ミス）した設備 — 元に戻せる */}
      {deleted && (
        <div className="mt-3 rounded-lg border border-zinc-200 bg-zinc-50/60 p-3" data-testid="dq-deleted-equipment">
          <p className="text-[12px] font-semibold text-zinc-800">
            削除した設備（{deleted.count}）
          </p>
          <p className="mt-0.5 text-[11px] text-zinc-500">{deleted.note}</p>
          {deleted.count === 0 ? (
            <p className="mt-1.5 text-[12px] text-zinc-400" data-testid="dq-deleted-equipment-empty">なし</p>
          ) : (
            <ul className="mt-1.5 space-y-1" data-testid="dq-deleted-equipment-rows">
              {deleted.rows.map((r) => (
                <li key={r.id} className="flex flex-wrap items-center gap-2 text-[12px]"
                  data-testid={`dq-deleted-equipment-${r.id}`}>
                  <span className="font-mono text-[11px] text-zinc-500">{r.asset_no || '番号なし'}</span>
                  <span className="font-medium text-zinc-900">{r.name}</span>
                  <span className="text-[11px] text-zinc-600">
                    {(r.deleted_at || '').slice(0, 10)}
                    {r.deleted_by ? ` · ${r.deleted_by}` : ''}
                    {r.delete_reason ? ` · ${r.delete_reason}` : ''}
                  </span>
                  <Button size="sm" variant="outline" className="ml-auto h-7 text-[11px]"
                    disabled={busy === r.id} onClick={() => restoreEquipment(r)}
                    data-testid={`dq-deleted-equipment-restore-${r.id}`}>
                    元に戻す
                  </Button>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}

      {rows.length === 0 ? (
        <p className="mt-3 text-[12px] text-zinc-500" data-testid="dq-empty">見つかりませんでした。</p>
      ) : (
        <ul className="mt-3 divide-y divide-zinc-100 rounded-lg border border-zinc-200" data-testid="dq-rows">
          {rows.slice(0, 200).map((r) => (
            <li key={r.key} className="px-3 py-2" data-testid={`dq-row-${r.key}`}>
              <div className="flex flex-wrap items-center gap-2 text-[12px]">
                <span className="rounded bg-zinc-100 px-1.5 text-[10px] font-semibold text-zinc-600">
                  {r.kind_label}
                </span>
                <span className="font-medium text-zinc-900">{r.label || '（名前なし）'}</span>
                <span className="text-zinc-500">{r.field_label}</span>
                <span className="font-mono text-rose-700">{String(r.current ?? '未設定')}</span>
                {r.fixable === false ? (
                  <span className="text-[11px] text-zinc-500" data-testid={`dq-manual-${r.key}`}>
                    設定画面で直してください
                  </span>
                ) : (
                <>
                <span className="text-zinc-400">→</span>
                <Input className="h-8 w-56 text-[12px]"
                  value={edits[r.key] !== undefined ? edits[r.key] : (r.proposed ?? '')}
                  placeholder={r.proposed ? '' : '直す値を入れてください（空にすると消します）'}
                  onChange={(e) => setEdits({ ...edits, [r.key]: e.target.value })}
                  data-testid={`dq-input-${r.key}`} />
                <Button size="sm" variant="outline" className="h-8 text-[11px]"
                  disabled={busy === r.key} onClick={() => fix(r)} data-testid={`dq-fix-${r.key}`}>
                  直す
                </Button>
                </>
                )}
              </div>
              <p className="mt-0.5 text-[10px] text-zinc-500">{r.reason}</p>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
};

export default DataQualityAudit;
